'use client';

/**
 * Data SPM DOM 打标：请求头 data-spm=1 时，在已接入 SPM 的挂载点真实 DOM 上
 * 输出 data-spm="完整编码"（与该挂载点事件上报的 spm_cnt 同源同值），供自动化
 * 核验工具比对；开关关闭或编码为空（表外静默同上报口径）时返回 undefined，
 * 展开后零 DOM 差异。
 *
 * 开关随文档请求的请求头解析（根布局经 ctxDataSpm 读取后下发），SPA 路由内保持
 * 不变。用法：
 *
 *   const domSpm = useSpmDomAttr(code);
 *   <a {...domSpm}>…</a>
 *
 * Provider 同时下发服务端判定的终端段（terminalKind）：App 内嵌判定依赖 UA，
 * 而服务端 SSR 期间读不到浏览器 UA（Node 的 navigator.userAgent 判不出 App），
 * 渲染期 B 段判定统一走 useSpmTerminalKind（context 优先、UA 兜底），保证 SSR
 * 与客户端打标/上报同值。
 */

import { createContext, useContext, type ReactNode } from 'react';

import { EPlatform } from '@/utils/enums/platform-enum';

import { resolveSpmTerminalKind, type SpmTerminalKind } from './terminal';

const DataSpmContext = createContext(false);
const SpmTerminalKindContext = createContext<SpmTerminalKind | undefined>(undefined);

export function DataSpmProvider({
  enabled,
  terminalKind,
  children,
}: {
  enabled: boolean;
  /** 服务端按请求 UA 预判的 SPM 终端段（pc/wap/app），供渲染期 B 段判定双端一致。 */
  terminalKind?: SpmTerminalKind;
  children: ReactNode;
}) {
  return (
    <DataSpmContext.Provider value={enabled}>
      <SpmTerminalKindContext.Provider value={terminalKind}>
        {children}
      </SpmTerminalKindContext.Provider>
    </DataSpmContext.Provider>
  );
}

export function useDataSpmEnabled(): boolean {
  return useContext(DataSpmContext);
}

/**
 * 渲染期 SPM 终端段判定：优先用 Provider 下发的服务端判定（请求 UA，双端一致），
 * 缺省（Provider 外/单测）回落客户端 UA 兜底。事件回调内（仅客户端触发）可继续
 * 用 resolveSpmTerminalKind 纯函数。
 */
export function useSpmTerminalKind(deviceType?: EPlatform): SpmTerminalKind {
  const contextKind = useContext(SpmTerminalKindContext);
  if (deviceType !== undefined) {
    // 显式 deviceType 能定死的分支（WEB→pc、IOS/ANDROID→app）以它为准；
    // 只剩「WAP 或未知」时才需要 App 内嵌兜底。
    if (
      deviceType === EPlatform.WEB ||
      deviceType === EPlatform.IOS ||
      deviceType === EPlatform.ANDROID
    ) {
      return resolveSpmTerminalKind(deviceType);
    }
  }
  return contextKind ?? resolveSpmTerminalKind(deviceType);
}

/**
 * 取挂载点 DOM 属性：开关开启且编码非空时返回 { 'data-spm': code }，否则
 * undefined。直接展开到目标真实元素（a/button/楼层容器等）上，不改事件与渲染结构。
 */
export function useSpmDomAttr(code: string | undefined | null): { 'data-spm': string } | undefined {
  const enabled = useContext(DataSpmContext);
  return enabled && code ? { 'data-spm': code } : undefined;
}
